import learningMaterial from 'ApiService/Teacher/learningMaterial';
import Card from 'components/card';
import InputField from 'components/fields/InputField';
import TextField from 'components/fields/TextField';
import { AuthContext } from 'contexts/AuthContext';
import ConfirmationModal from 'pages/Common/ConfirmationModal';
import Nodatafound from 'pages/Common/Nodatafound';
import { useContext, useEffect, useState } from 'react';
import toast from 'react-hot-toast';

const AttendancesReportData = ({ selectedClass, courses, loadingCourses }) => {
  useContext(AuthContext);
  const [getlinks, setGetlinks] = useState([]);
  const [editingMaterial, setEditingMaterial] = useState(null);
  const [selectedCourse, setSelectedCourse] = useState(null);
  const [loading, setLoading] = useState(true);
  const [editData, setEditData] = useState({
    title: '',
    description: '',
    Url: '',
    courseId: '',
    batchId: '',
  });

  const [isDeleteModalOpen, setIsDeleteModalOpen] = useState(false);
  const [isEditModalOpen, setIsEditModalOpen] = useState(false);
  const [isSaveModalOpen, setIsSaveModalOpen] = useState(false);
  const [currentMaterialId, setCurrentMaterialId] = useState(null);

  const fetchData = async () => {
    setLoading(true);
    try {
      const payload = {
        courseId: selectedClass.courseId,
        batchId: selectedClass.batchId,
      };
      const response = await learningMaterial.getLectureVideoLinks(payload);
      if (response.data.success) {
        setGetlinks(response.data.videos);
      } else {
        setGetlinks([]);
        toast.error('No attendance report found for the selected date.');
      }
    } catch (error) {
      setGetlinks([]);
    } finally {
      setLoading(false);
    }
  };

  const handleEditClick = (material) => {
    setEditData({
      title: material.title,
      description: material.description,
      Url: material.Url,
      courseId: material.course,
      batchId: material.batch,
    });

    const selected = courses.find(
      (course) => course.courseId === material.course
    );
    setSelectedCourse(selected);

    setCurrentMaterialId(material._id);
    setEditingMaterial(material._id);
    setIsEditModalOpen(false);
  };

  const confirmEdit = () => {
    setEditingMaterial(currentMaterialId);
    setIsEditModalOpen(false);
  };

  const handleFormChange = (e) => {
    const { name, value } = e.target;
    if (!name) return;
    setEditData((prev) => ({ ...prev, [name]: value }));
  };

  const handleClassChange = (e) => {
    const courseId = e.target.value;
    const selected = courses.find((course) => course.courseId === courseId);
    setSelectedCourse(selected);
    setEditData((prev) => ({ ...prev, courseId, batchId: '' }));
  };

  const handleChange = (e) => {
    const { name, value } = e.target;
    setEditData((prev) => ({ ...prev, [name]: value }));
  };

  const handleSaveClick = (materialId) => {
    setCurrentMaterialId(materialId);
    setIsSaveModalOpen(true);
  };

  const confirmSave = async () => {
    try {
      const updatedMaterial = await learningMaterial.updateLectureVideoLinks(
        editData,
        currentMaterialId
      );
      if (updatedMaterial.data.success) {
        setGetlinks((prev) =>
          prev.map((item) =>
            item._id === currentMaterialId ? { ...item, ...editData } : item
          )
        );
        setEditingMaterial(null);
        toast.success('Material updated successfully.');
      } else {
        toast.error('Failed to update material.');
      }
    } catch (error) {
      toast.error('An error occurred while updating.');
    } finally {
      setIsSaveModalOpen(false);
    }
  };

  const handleDeleteClick = (materialId) => {
    setCurrentMaterialId(materialId);
    setIsDeleteModalOpen(true);
  };

  const confirmDelete = async () => {
    try {
      const response = await learningMaterial.deleteVideoUrl(currentMaterialId);
      if (response.data.success) {
        setGetlinks((prev) =>
          prev.filter((item) => item._id !== currentMaterialId)
        );
        toast.success('Material deleted successfully.');
      } else {
        toast.error('Failed to delete material.');
      }
    } catch (error) {
      toast.error('An error occurred while deleting.');
    } finally {
      setIsDeleteModalOpen(false);
    }
  };

  useEffect(() => {
    if (selectedClass.courseId && selectedClass.batchId) {
      fetchData();
    } else {
      toast('Please Select Class Details');
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [selectedClass]);

  if (loading) {
    return (
      <Card
        extra={'w-full sm:overflow-auto p-4 flex justify-center items-center'}
      >
        <div className="h-10 w-10 animate-spin rounded-full border-b-2 border-gray-900 dark:border-white"></div>
      </Card>
    );
  }

  return (
    <Card extra={'w-full sm:overflow-auto p-4'}>
      <div className="space-y-4">
        {getlinks.length === 0 ? (
          <div className="py-10 text-center text-gray-500">
            <Nodatafound title="No Lecture Url found for this class." />
          </div>
        ) : (
          <div className="space-y-4">
            {getlinks.map((material) => (
              <div
                key={material._id}
                className="overflow-hidden rounded-lg border border-gray-200 p-4 shadow-sm"
              >
                {editingMaterial === material._id ? (
                  <div>
                    <InputField
                      label="Title"
                      type="text"
                      name="title"
                      value={editData.title}
                      onChange={handleFormChange}
                      className="mb-2 border p-2"
                    />
                    <TextField
                      label="Description"
                      type="text"
                      name="description"
                      id="description"
                      value={editData?.description || ''}
                      onChange={handleFormChange}
                      className="mb-2 w-full border p-2"
                    />
                    <InputField
                      label="Video Url"
                      type="text"
                      name="Url"
                      value={editData.Url}
                      onChange={handleFormChange}
                      className="mb-2 w-full border p-2"
                    />

                    {/* Course and Batch Select */}
                    <div className="grid grid-cols-1 gap-4 md:grid-cols-2">
                      <div>
                        <label className="ml-3 text-sm font-bold text-navy-700 dark:text-white">
                          Course*
                        </label>
                        <select
                          name="courseId"
                          value={editData.courseId}
                          onChange={handleClassChange}
                          className="mt-2 w-full rounded-xl border border-gray-200 p-3 text-sm outline-none dark:border-white/10 dark:bg-navy-800 dark:text-white"
                        >
                          <option value="">Select Course</option>
                          {loadingCourses ? (
                            <option>Loading courses...</option>
                          ) : courses.length === 0 ? (
                            <option className="cursor-pointer font-bold text-brand-500">
                              No courses available
                            </option>
                          ) : (
                            courses.map((course, i) => (
                              <option
                                key={course.courseId}
                                value={course.courseId}
                              >
                                {i + 1}. {course.courseName}
                              </option>
                            ))
                          )}
                        </select>
                      </div>

                      <div>
                        <label className="text-sm font-bold text-navy-700 dark:text-white">
                          Batch*
                        </label>
                        <select
                          name="batchId"
                          value={editData.batchId}
                          onChange={handleChange}
                          className="mt-2 w-full rounded-xl border border-gray-200 p-3 text-sm outline-none dark:border-white/10 dark:bg-navy-800 dark:text-white"
                        >
                          <option value="">Select Batch</option>
                          {selectedCourse?.batches.map((batch) => (
                            <option key={batch._id} value={batch._id}>
                              Batch {batch.batchNumber} -{' '}
                              {new Date(batch.startDate).toLocaleDateString()}
                              to {new Date(batch.endDate).toLocaleDateString()}
                            </option>
                          ))}
                        </select>
                      </div>
                    </div>

                    <div className="mt-4 flex space-x-4">
                      <button
                        onClick={() => handleSaveClick(material._id)}
                        className="rounded bg-blue-500 px-4 py-2 text-white hover:bg-blue-600"
                      >
                        Save
                      </button>
                      <button
                        onClick={() => setEditingMaterial(null)}
                        className="rounded bg-gray-300 px-4 py-2 text-gray-800 hover:bg-gray-400"
                      >
                        Cancel
                      </button>
                    </div>
                  </div>
                ) : (
                  <div>
                    <div className="flex justify-between">
                      <div>
                        <h2 className="text-lg font-semibold text-gray-800 dark:text-white">
                          {material.title}
                        </h2>
                        <p className="text-sm text-gray-600 dark:text-gray-300">
                          {material.description}
                        </p>
                        <a
                          href={material.Url}
                          target="_blank"
                          rel="noopener noreferrer"
                          className="text-sm text-blue-600 hover:underline dark:text-blue-400"
                        >
                          View Link
                        </a>
                      </div>
                      <span className="text-xs text-gray-500">
                        {new Date(material.createdAt).toLocaleDateString()}
                      </span>
                    </div>
                    <div className="mt-2 flex items-center text-xs text-gray-500">
                      <span>
                        Teacher: {material.teacher?.name || 'Not specified'}
                      </span>
                    </div>
                    <div className="mt-3 flex space-x-4">
                      <button
                        onClick={() => handleEditClick(material)}
                        className="rounded bg-blue-100 px-3 py-1 text-blue-700 hover:bg-blue-200 dark:bg-blue-900 dark:text-blue-200"
                      >
                        Edit
                      </button>
                      <button
                        onClick={() => handleDeleteClick(material._id)}
                        className="rounded bg-red-100 px-3 py-1 text-red-700 hover:bg-red-200 dark:bg-red-900 dark:text-red-200"
                      >
                        Delete
                      </button>
                    </div>
                  </div>
                )}
              </div>
            ))}
          </div>
        )}
      </div>

      {/* Confirmation Modals */}
      <ConfirmationModal
        isOpen={isDeleteModalOpen}
        onClose={() => setIsDeleteModalOpen(false)}
        onConfirm={confirmDelete}
        title="Confirm Delete"
        message="Are you sure you want to delete this lecture link? This action cannot be undone."
        confirmText="Yes, Delete"
        cancelText="Cancel"
        icon="warning"
      />

      <ConfirmationModal
        isOpen={isEditModalOpen}
        onClose={() => setIsEditModalOpen(false)}
        onConfirm={confirmEdit}
        title="Confirm Edit"
        message="Do you want to edit this lecture link?"
        confirmText="Yes, Edit"
        cancelText="Cancel"
        icon="info"
      />

      <ConfirmationModal
        isOpen={isSaveModalOpen}
        onClose={() => setIsSaveModalOpen(false)}
        onConfirm={confirmSave}
        title="Confirm Save Changes"
        message="Are you sure you want to save these changes?"
        confirmText="Yes, Save Changes"
        cancelText="Cancel"
        icon="info"
      />
    </Card>
  );
};

export default AttendancesReportData;
